import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ComponentProps,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  buildSiteComplianceEvaluationExportRows,
  getSiteComplianceEvaluationReportFilename,
  fetchSiteComplianceEvaluationDetails,
  useSiteDetailQuery,
  useSiteComplianceEvaluationsQuery,
} from '@/domains/siteCompliance';
import { exportToCsv } from '@/lib/utils';
import { useMobileRouteLoading, useResponsiveViewport } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';

import {
  buildSiteComplianceListPath,
  buildSiteComplianceEvaluationPath,
} from '../../siteComplianceRouting';
import type SiteDetailFacts from './SiteDetailFacts';
import type SiteDetailHeader from './SiteDetailHeader';
import type SiteDetailHistory from './SiteDetailHistory';
import type SiteDetailToolbar from './SiteDetailToolbar';
import type SiteDetailTrend from './SiteDetailTrend';
import type RunEvaluationDialog from '../../components/RunEvaluationDialog';

interface UseSiteDetailControllerResult {
  emptyStateMessage: string | null;
  errorMessage: string | null;
  factsProps: ComponentProps<typeof SiteDetailFacts>;
  headerProps: ComponentProps<typeof SiteDetailHeader>;
  historyProps: ComponentProps<typeof SiteDetailHistory>;
  isMobileViewport: boolean;
  runEvaluationProps: ComponentProps<typeof RunEvaluationDialog>;
  toolbarProps: ComponentProps<typeof SiteDetailToolbar>;
  trendProps: ComponentProps<typeof SiteDetailTrend>;
}

export const useSiteDetailController =
  (): UseSiteDetailControllerResult => {
    const location = useLocation();
    const navigate = useNavigate();
    const { siteId = '' } = useParams();
    const isMobileViewport = useResponsiveViewport();
    const { showToast } = useToast();
    const siteDetailQuery = useSiteDetailQuery(siteId);
    const evaluationsQuery = useSiteComplianceEvaluationsQuery({
      latestOnly: false,
      pageSize: 10,
      siteIds: siteId ? [siteId] : [],
    });
    const [activeEvaluationId, setActiveEvaluationId] = useState<string | null>(
      null,
    );
    const [isExportingCsv, setIsExportingCsv] = useState(false);
    const [isExportingHistoryCsv, setIsExportingHistoryCsv] = useState(false);
    const [isRunEvaluationOpen, setIsRunEvaluationOpen] = useState(false);

    const evaluationHistory = evaluationsQuery.evaluations;
    const isHistoryLoading =
      evaluationsQuery.isLoading || evaluationsQuery.isFetching;

    useMobileRouteLoading(
      siteDetailQuery.isLoading ||
        siteDetailQuery.isFetching ||
        isHistoryLoading,
    );

    useEffect(() => {
      if (evaluationsQuery.isError) {
        console.error('Site Compliance evaluation history failed to load', {
          error: evaluationsQuery.error,
          evaluationId: activeEvaluationId,
          jobId: null,
          siteId,
        });
        showToast('error', {
          title: 'Error loading evaluation history',
          message: 'Site Compliance evaluation history could not be loaded.',
        });
      }
    }, [
      activeEvaluationId,
      evaluationsQuery.error,
      evaluationsQuery.isError,
      showToast,
      siteId,
    ]);

    const selectedSite = siteDetailQuery.data?.site ?? null;

    const latestEvaluation = evaluationHistory[0] ?? null;

    const evaluationTrendData = useMemo(
      () =>
        evaluationHistory
          .slice()
          .reverse()
          .map((evaluation) => ({
            generatedAt: evaluation.generatedAt,
            score: evaluation.score,
          })),
      [evaluationHistory],
    );

    const handleBack = useCallback(() => {
      navigate(buildSiteComplianceListPath(location.pathname));
    }, [location.pathname, navigate]);

    const handleOpenRunEvaluation = useCallback(() => {
      if (!selectedSite) {
        return;
      }

      setIsRunEvaluationOpen(true);
    }, [selectedSite]);

    const handleViewEvaluation = useCallback(
      (evaluationId: string) => {
        navigate(buildSiteComplianceEvaluationPath(location.pathname, evaluationId));
      },
      [location.pathname, navigate],
    );

    const handleDownloadEvaluation = useCallback(
      async (evaluationId: string) => {
        if (!selectedSite) {
          return;
        }

        const evaluationDetails =
          await fetchSiteComplianceEvaluationDetails(evaluationId);

        if (!evaluationDetails?.evaluation) {
          return;
        }

        setActiveEvaluationId(evaluationId);
        setIsExportingCsv(true);

        try {
          await exportToCsv({
            fileName: getSiteComplianceEvaluationReportFilename({
              evaluationId: evaluationDetails.evaluation.id,
              siteId: evaluationDetails.site.siteId,
            }),
            options: {},
            rows: buildSiteComplianceEvaluationExportRows({
              evaluation: evaluationDetails.evaluation,
              site: evaluationDetails.site,
            }),
          });

          showToast('success', {
            message: 'Site evaluation report exported successfully',
          });
        } catch (error) {
          console.error('Site Compliance evaluation report export failed', {
            error,
            evaluationId,
            jobId: null,
            siteId: selectedSite.siteId,
          });
          showToast('error', {
            title: 'Error exporting site evaluation',
            message: 'An error occurred while exporting the evaluation report.',
          });
        } finally {
          setActiveEvaluationId(null);
          setIsExportingCsv(false);
        }
      },
      [selectedSite, showToast],
    );

    const handleExportHistory = useCallback(async () => {
      if (!selectedSite) {
        return;
      }

      setIsExportingHistoryCsv(true);

      try {
        if (evaluationHistory.length === 0) {
          showToast('warning', {
            title: 'No history to export',
            message: 'No loaded evaluations are available for this site.',
          });
          return;
        }

        await exportToCsv({
          fileName: `site-compliance-history-${selectedSite.siteId}-${new Date().toISOString()}`,
          options: {},
          rows: evaluationHistory.map((evaluation) => ({
            evaluationId: evaluation.id,
            exceptions: evaluation.counts.exceptions,
            failed: evaluation.counts.failed,
            generatedAt: evaluation.generatedAt,
            passed: evaluation.counts.passed,
            score: evaluation.score,
            scored: evaluation.counts.scored,
            siteId: evaluation.siteId,
            skipped: evaluation.counts.skipped,
            total: evaluation.counts.total,
            unscored: evaluation.counts.unscored,
          })),
        });

        showToast('success', {
          message: 'Site evaluation history exported successfully',
        });
      } catch (error) {
        console.error('Site Compliance history export failed', {
          error,
          evaluationId: activeEvaluationId,
          jobId: null,
          siteId: selectedSite.siteId,
        });
        showToast('error', {
          title: 'Error exporting history',
          message: 'An error occurred while exporting the loaded history.',
        });
      } finally {
        setIsExportingHistoryCsv(false);
      }
    }, [activeEvaluationId, evaluationHistory, selectedSite, showToast]);

    return {
      emptyStateMessage:
        !siteDetailQuery.isLoading &&
        !siteDetailQuery.isFetching &&
        !isHistoryLoading &&
        !evaluationsQuery.isError &&
        !selectedSite
          ? `Site ${siteId} was not found in Site Compliance.`
          : null,
      errorMessage:
        !isHistoryLoading && evaluationsQuery.isError
          ? 'Site Compliance evaluation history could not be loaded.'
          : null,
      factsProps: {
        latestEvaluation,
        site: selectedSite,
      },
      headerProps: {
        isMobileViewport,
        onBack: handleBack,
        onRunEvaluation: handleOpenRunEvaluation,
        site: selectedSite,
      },
      historyProps: {
        activeEvaluationId,
        errorMessage:
          !isHistoryLoading && evaluationsQuery.isError
            ? 'Site Compliance evaluation history could not be loaded.'
            : null,
        evaluations: evaluationHistory,
        hideHeader: !isMobileViewport,
        hasNextPage: evaluationsQuery.hasNextPage,
        isExportingCsv,
        isExportingHistoryCsv,
        isFetchingNextPage: evaluationsQuery.isFetchingNextPage,
        isLoading: isHistoryLoading,
        isMobileViewport,
        onDownloadEvaluation: handleDownloadEvaluation,
        onExportHistory: handleExportHistory,
        onLoadMore: () => void evaluationsQuery.fetchNextPage(),
        onViewEvaluation: handleViewEvaluation,
      },
      isMobileViewport,
      runEvaluationProps: {
        isOpen: isRunEvaluationOpen,
        onClose: () => setIsRunEvaluationOpen(false),
        siteId: selectedSite?.siteId ?? null,
        siteName: selectedSite?.siteName ?? null,
      },
      toolbarProps: {
        isExportingHistoryCsv,
        onExportHistory: handleExportHistory,
        onRunEvaluation: handleOpenRunEvaluation,
      },
      trendProps: {
        evaluationTrendData,
        hasEvaluationTrendData: evaluationTrendData.length > 0,
      },
    };
  };
